// src/App.js
import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Layout
import MainLayout from './components/Layout/MainLayout';

// Pages
import Dashboard from './pages/Dashboard';
import Login from './pages/Auth/Login';
import Register from './pages/Auth/Register';
import ForgotPassword from './pages/Auth/ForgotPassword';
import ResetPassword from './pages/Auth/ResetPassword';
import Websites from './pages/Websites';
import WebsiteDetail from './pages/WebsiteDetail';
import Reports from './pages/Reports';
import Notifications from './pages/Notifications';
import Integrations from './pages/Integrations';
import UserProfile from './pages/UserProfile';
import Subscription from './pages/Subscription';
import Credits from './pages/Credits';
import NotFound from './pages/NotFound';

// Auth protection component
import AuthRoute from './components/Auth/AuthRoute';

// Toast componenty
import { ToastContainer } from './components/UI/Toast';
import ErrorBoundary from './components/ErrorBoundary';

// Kontext pro stav sidebaru
import { createContext, useContext } from 'react';
import Security from "./pages/Security";
import ApiDocumentation from "./pages/ApiDocumentation";
import Teams from "./pages/Teams";
import TeamDetail from "./pages/TeamDetail";

// Vytvoření kontextu pro stav sidebaru
export const SidebarContext = createContext();

// Vytvoření QueryClient instance s konfigurací
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minut
    },
  },
});

// Kontrola, zda je aktivován tmavý režim
const checkDarkMode = () => {
  const isDarkMode = localStorage.getItem('darkMode') === 'true';
  if (isDarkMode) {
    document.body.classList.add('dark-mode');
  }
};

const App = () => {
  // Stav pro rozbalení/sbalení sidebaru a jeho poskytnutí přes kontext
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
      localStorage.getItem('sidebarCollapsed') === 'true' || window.innerWidth < 992
  );

  // Aplikace tmavého režimu při načtení
  useEffect(() => {
    checkDarkMode();

    // Uložení stavu sidebaru do localStorage
    const handleSidebarChange = () => {
      localStorage.setItem('sidebarCollapsed', sidebarCollapsed);
    };

    handleSidebarChange();

    // Nastavení stavu podle velikosti okna
    const handleResize = () => {
      if (window.innerWidth < 992) {
        setSidebarCollapsed(true);
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [sidebarCollapsed]);

  return (
      <SidebarContext.Provider value={{ collapsed: sidebarCollapsed, setCollapsed: setSidebarCollapsed }}>
        <QueryClientProvider client={queryClient}>
          <ErrorBoundary>
            <BrowserRouter>
              <Routes>
                {/* Veřejné cesty */}
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password/:token" element={<ResetPassword />} />

                {/* Chráněné cesty */}
                <Route path="/" element={<AuthRoute><MainLayout /></AuthRoute>}>
                  <Route index element={<Navigate to="/dashboard" />} />
                  <Route path="dashboard" element={<Dashboard />} />
                  <Route path="websites" element={<Websites />} />
                  <Route path="security" element={<Security />} />
                  <Route path="websites/:id/*" element={<WebsiteDetail />} />
                  <Route path="reports" element={<Reports />} />
                  <Route path="notifications" element={<Notifications />} />
                  <Route path="integrations" element={<Integrations />} />
                  <Route path="profile" element={<UserProfile />} />
                  <Route path="subscription" element={<Subscription />} />
                  <Route path="credits" element={<Credits />} />
                  <Route path="api-documentation" element={<ApiDocumentation />} />

                  <Route path="teams" element={<Teams />} />  {/* Nová routa pro stránku týmů */}
                  <Route path="teams/:id" element={<TeamDetail />} />
                  <Route path="*" element={<NotFound />} />
                </Route>
              </Routes>

              {/* Globální toast notifikace */}
              <ToastContainer />
            </BrowserRouter>
          </ErrorBoundary>
        </QueryClientProvider>
      </SidebarContext.Provider>
  );
};

// Custom hook pro použití kontextu sidebaru
export const useSidebar = () => useContext(SidebarContext);

export default App;